import { Link } from 'react-router'
import { PageHeader } from '@/components/data/page-header'
import { Horizon } from '@/components/data/horizon'
import { FlagChips, PlanBadge } from '@/components/data/badges'
import { ErrorState, Warnings } from '@/components/data/states'
import { Person } from '@/components/data/person'
import { Skeleton } from '@/components/ui/skeleton'
import { TooltipProvider } from '@/components/ui/tooltip'
import { formatCompact, formatMoney, formatNumber, formatRelative } from '@/lib/format'
import { useOverview } from '@/lib/queries'
import type { Overview } from '@/lib/types'

function Ledger({ data }: { data: Overview }) {
  const t = data.totals
  const cells = [
    { to: '/teams', label: 'Teams', value: formatNumber(t.teams), note: `${formatNumber(t.memberships)} memberships` },
    { to: '/users', label: 'Accounts', value: formatNumber(t.users), note: 'in Firebase' },
    {
      to: '/subscriptions',
      label: 'Paying teams',
      value: formatNumber(t.paid_teams),
      note: `${formatNumber(t.subscriptions.expiring + t.subscriptions.canceling)} ending soon`,
    },
    { to: '/subscriptions', label: 'Monthly recurring', value: formatMoney(t.subscriptions.mrr_cents), note: 'at list price' },
    { to: '/teams?sort=companies', label: 'Companies tracked', value: formatCompact(t.companies), note: `${formatCompact(t.snapshots)} history rows` },
    {
      to: '/teams?attention=1',
      label: 'Need attention',
      value: formatNumber(t.need_attention),
      note: t.need_attention ? 'review them' : 'all clear',
      tone: t.need_attention ? 'text-risk' : undefined,
    },
  ]
  return (
    <dl className="panel grid grid-cols-2 divide-x divide-y divide-border overflow-hidden sm:grid-cols-3 lg:grid-cols-6 lg:divide-y-0 [&>*]:border-border">
      {cells.map((c) => (
        <Link key={c.label} to={c.to} className="group block p-4 hover:bg-accent/40 focus-visible:-outline-offset-2">
          <dt className="text-muted-foreground">{c.label}</dt>
          <dd className={`figures mt-1 text-2xl font-semibold tracking-tight ${c.tone ?? ''}`}>{c.value}</dd>
          <dd className="mt-0.5 text-xs text-muted-foreground">{c.note}</dd>
        </Link>
      ))}
    </dl>
  )
}

function Signups({ data }: { data: Overview }) {
  const max = Math.max(1, ...data.signups.map((d) => d.count))
  const last30 = data.signups.slice(-30).reduce((sum, d) => sum + d.count, 0)
  const bar = 6
  return (
    <section className="panel p-4" aria-labelledby="signups">
      <h2 id="signups" className="font-semibold">
        New accounts
      </h2>
      <p className="text-muted-foreground">
        <span className="figures font-medium text-foreground">{last30}</span> in the last 30 days
      </p>
      <svg viewBox={`0 0 ${data.signups.length * bar} 40`} preserveAspectRatio="none" className="mt-3 h-10 w-full" role="img" aria-label="Accounts created per day over the last 60 days">
        {data.signups.map((d, i) => {
          const h = d.count === 0 ? 1 : Math.max(3, (d.count / max) * 38)
          return <rect key={d.date} x={i * bar + 1} y={40 - h} width={bar - 2} height={h} rx="1" fill={d.count ? 'var(--primary)' : 'var(--border)'} opacity={d.count ? 0.85 : 1} />
        })}
      </svg>
      <ul className="mt-4 space-y-3">
        {data.recent_users.length === 0 && <li className="text-muted-foreground">No accounts yet.</li>}
        {data.recent_users.map((u) => (
          <li key={u.uid} className="flex items-center justify-between gap-3">
            <Link to={`/users?user=${encodeURIComponent(u.uid)}&search=${encodeURIComponent(u.email)}`} className="min-w-0 rounded hover:opacity-80">
              <Person name={u.name} email={u.email} uid={u.uid} />
            </Link>
            <span className="shrink-0 text-xs text-muted-foreground">{formatRelative(u.created_at)}</span>
          </li>
        ))}
      </ul>
    </section>
  )
}

function Attention({ data }: { data: Overview }) {
  return (
    <section className="panel p-4" aria-labelledby="attention">
      <div className="flex items-baseline justify-between">
        <h2 id="attention" className="font-semibold">
          Needs attention
        </h2>
        {data.totals.need_attention > data.attention.length && (
          <Link to="/teams?attention=1" className="text-primary hover:underline">
            See all {data.totals.need_attention}
          </Link>
        )}
      </div>
      {data.attention.length === 0 ? (
        <p className="mt-3 text-muted-foreground">
          Nothing right now. Teams near a limit, with a failed payment, a broken HubSpot sync or a subscription about to end will show up here.
        </p>
      ) : (
        <ul className="mt-2 divide-y">
          {data.attention.map((t) => (
            <li key={t.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5 py-2.5">
              <div className="flex min-w-0 items-center gap-2">
                <Link to={`/teams/${t.id}`} className="truncate font-medium hover:text-primary hover:underline">
                  {t.name}
                </Link>
                <PlanBadge planId={t.plan_id} name={t.plan_name} trial={t.subscription.source === 'trial'} />
              </div>
              <FlagChips flags={t.flags} max={3} />
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function Loading() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-24 w-full rounded-xl" />
      <Skeleton className="h-64 w-full rounded-xl" />
      <div className="grid gap-4 lg:grid-cols-2">
        <Skeleton className="h-64 rounded-xl" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    </div>
  )
}

export default function OverviewPage() {
  const { data, error, isPending, refetch } = useOverview()

  return (
    <TooltipProvider>
      <PageHeader title="Overview">{data ? <>Updated {formatRelative(data.generated_at)}</> : 'Every team and account in one place.'}</PageHeader>
      {isPending ? (
        <Loading />
      ) : error ? (
        <div className="panel">
          <ErrorState error={error} onRetry={() => refetch()} />
        </div>
      ) : (
        <div className="space-y-4">
          <Warnings warnings={data.warnings} />
          <Ledger data={data} />

          <section className="panel p-4 pb-3" aria-labelledby="horizon">
            <h2 id="horizon" className="font-semibold">
              How full each team is
            </h2>
            <p className="mb-3 max-w-2xl text-muted-foreground">
              Each building is a team. The taller it stands, the more of its plan’s company limit it uses. Select one to open it.
            </p>
            {data.horizon.length === 0 ? <p className="py-8 text-muted-foreground">No teams yet.</p> : <Horizon teams={data.horizon} />}
          </section>

          <div className="grid items-start gap-4 lg:grid-cols-[1.4fr_1fr]">
            <Attention data={data} />
            <Signups data={data} />
          </div>
        </div>
      )}
    </TooltipProvider>
  )
}
